// Sellside buyer outreach tracker (backend: routes/outreach.py → app/outreach.py).
//
// The team's Excel tracker, on the deal: one row per person we emailed,
// grouped by firm. Firm facts (buyer status, category, NDA, CIM, interest /
// pass reason, BS call, LOI) live on the deal's buyer list — the same rows as
// the Interested buyers board — so they show once per firm and an edit on one
// row is the firm's. The tracker spreadsheet uploads as is (preview → apply).
//
//   window.OutreachSection       deal page card: KPIs + Open tracker / Upload
//   window.OutreachTracker       the full-width tracker (#/deals/<id>?tab=outreach);
//                                desktop and iPad only — deal_detail.jsx never
//                                mounts it on a phone
//   window.OutreachImportDrawer  upload a buyer list / tracker
//
// Email activity (first email, follow-ups, replies, bounces) is read live from
// the deal's linked SuperSend campaign(s) and synced Outlook mail — linking
// the campaign also stops it running as seller marketing.

const OUTREACH_CHUNK = 100;   // routes/outreach.py APPLY_CHUNK

const OUTREACH_STATUS_HUE = {
  not_contacted: 'slate', emailed: 'blue', followed_up: 'blue', bounced: 'amber', replied: 'violet',
  nda_sent: 'amber', nda_redlined: 'amber', nda_signed: 'green', cim_sent: 'green', ioi: 'teal', mgmt_meeting: 'teal',
  loi: 'green', passed: 'rose',
};
// Amounts as people type them on a tracker: "4M", "$4.5m", "750k", "4–5M"
// (a range; the unit carries over), "4,000,000 - 5,000,000".
const parseAmount = (t, unit) => {
  const m = /^\$?\s*([\d,]*\.?\d+)\s*([kmb])?$/i.exec((t || '').trim());
  if (!m) return null;
  const u = (m[2] || unit || '').toLowerCase();
  return Number(m[1].replace(/,/g, '')) * ({ k: 1e3, m: 1e6, b: 1e9 }[u] || 1);
};
const parseRange = (text) => {
  const t = (text || '').trim();
  if (!t) return { low: null, high: null };
  const parts = t.split(/\s*(?:-|–|—|to)\s*/i).filter(Boolean);
  const unit = ((/([kmb])\s*$/i.exec(parts[parts.length - 1] || '') || [])[1]) || '';
  const low = parseAmount(parts[0], unit);
  const high = parts[1] ? parseAmount(parts[1], unit) : low;
  if (low == null || high == null) throw new Error(`“${text}” isn’t an amount — try 4.5M or 4–5M`);
  return { low: Math.min(low, high), high: Math.max(low, high) };
};
const fmtRange = (lo, hi) => (lo == null && hi == null ? null
  : lo === hi || hi == null ? window.fmtMoney(lo) : `${window.fmtMoney(lo)}–${window.fmtMoney(hi)}`);
const BUYER_STATUS_OPTS = [
  { value: 'pb', label: 'PB', hue: 'violet' },
  { value: 'peg', label: 'PEG', hue: 'blue' },
  { value: 'umbrella', label: 'Umbrella', hue: 'teal' },
  { value: 'unaffiliated', label: 'Unaffiliated', hue: 'slate' },
];
const BUYER_STATUS_LABEL = Object.fromEntries(BUYER_STATUS_OPTS.map((o) => [o.value, o]));
const OUTREACH_NDA_OPTS = [
  { value: 'not_sent', label: 'not sent', hue: 'slate' },
  { value: 'sent', label: 'sent', hue: 'amber' },
  { value: 'redlined', label: 'redlined', hue: 'amber' },
  { value: 'signed', label: 'signed', hue: 'green' },
  { value: 'declined', label: 'declined', hue: 'rose' },
];
const OUTREACH_PASS_OPTS = [
  { value: 'not_interested', label: 'not interested' },
  { value: 'too_small', label: 'too small' }, { value: 'geography', label: 'geography' },
  { value: 'valuation', label: 'valuation' }, { value: 'wrong_industry', label: 'wrong industry' },
  { value: 'other', label: 'other' }, { value: '', label: '— clear —' },
];
// Filter chips → the row statuses they cover.
const OUTREACH_FILTERS = [
  ['all', 'All', null],
  ['suggested', 'Suggested updates', (r) => (r.firm.suggestions || []).length > 0],
  ['open', 'Still in play', (r) => r.status !== 'passed'],
  ['not_contacted', 'Not contacted', (r) => r.status === 'not_contacted'],
  ['emailed', 'Emailed, no reply', (r) => r.status === 'emailed' || r.status === 'followed_up'],
  ['waiting', 'Waiting on us', (r) => !!(r.activity && r.activity.waiting_on_us) && r.status !== 'passed'],
  ['replied', 'Replied', (r) => r.status === 'replied' || !!(r.activity && r.activity.replied_at)],
  ['nda', 'NDA stage', (r) => ['nda_sent', 'nda_redlined', 'nda_signed'].includes(r.status)],
  ['nda_signed', 'NDA signed', (r) => r.firm.nda_status === 'signed'],
  ['bids', 'IOI / LOI', (r) => ['ioi', 'mgmt_meeting', 'loi'].includes(r.status)],
  ['cim', 'CIM sent', (r) => r.firm.cim_sent],
  ['passed', 'Passed', (r) => r.status === 'passed'],
];

const fmtOutreachDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return y && m && d ? `${Number(m)}/${Number(d)}/${y.slice(2)}` : null;
};

const useOutreach = (dealId) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const reload = React.useCallback(() => {
    if (!dealId) return Promise.resolve();
    return window.AtlasApi.outreachTracker(dealId)
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message || 'Could not load the tracker'));
  }, [dealId]);
  React.useEffect(() => { setData(null); reload(); }, [reload]);
  return { data, error, reload };
};

const OutreachKpis = ({ k, compact = false }) => {
  if (!k) return null;
  const tiles = [
    ['Buyers', k.firms], compact ? null : ['People', k.contacts], ['Emailed', k.emailed], ['Followed up', k.followed_up],
    ['Replied', k.replied], ['Waiting on us', k.waiting_on_us], ['Passed', k.passed], ['NDA sent', k.nda_sent],
    ['NDA signed', k.nda_signed], ['CIM sent', k.cim_sent], ['IOIs', k.ioi], ['LOIs', k.loi],
  ].filter((t) => t && t[1] !== undefined);
  return (
    <div style={{ display: 'flex', gap: compact ? 14 : 18, flexWrap: 'wrap' }}>
      {tiles.map(([label, v]) => (
        <div key={label} style={{ minWidth: compact ? 52 : 64 }}>
          <div className="num" style={{ fontSize: compact ? 16 : 18, fontWeight: 650, color: v ? 'var(--text)' : 'var(--text-4)', fontFamily: 'var(--font-display)' }}>
            {(v || 0).toLocaleString()}
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{label}</div>
        </div>
      ))}
    </div>
  );
};

// ── Deal page card ─────────────────────────────────────────────────────────
// Shows on every seller deal, whatever its type or approach — a targeted
// (buyside) process still emails buyers (Project Evergreen, 2026-10-02).
// Buyer pursuits are the one exception: they aren't seller deals. Before
// anything is tracked it's a one-line card with the start buttons (open by
// default on sell-side deals, folded elsewhere — the fold is remembered).
const OutreachSection = ({ deal, onOpen }) => {
  const { data, reload } = useOutreach(deal && deal.id);
  const [importing, setImporting] = React.useState(false);
  const [linking, setLinking] = React.useState(false);
  if (!deal || !data) return null;
  const k = data.kpis || {};
  const campaigns = data.campaigns || [];
  const tracking = (k.contacts || 0) > 0 || campaigns.length > 0;
  if (deal.type === 'buyer_buyside') return null;
  const drawer = (
    <>
      {importing && <window.OutreachImportDrawer deal={deal} open onClose={() => setImporting(false)} onDone={reload} />}
      {linking && <OutreachCampaignDrawer deal={deal} onClose={() => setLinking(false)} onLinked={reload} />}
    </>
  );
  if (!tracking) {
    return (
      <Section title="Buyer outreach" collapsible id="deal.outreach" preview="not started"
        defaultOpen={deal.type === 'sell_side'}
        action={(
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <Btn size="xs" variant="outline" icon={<Icon.Upload size={11} />} onClick={() => setImporting(true)}>Upload list</Btn>
            <Btn size="xs" variant="ghost" onClick={onOpen}>Open tracker</Btn>
          </span>
        )}>
        <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <Btn size="sm" variant="outline" icon={<Icon.Mail size={12} />} onClick={() => setLinking(true)}>Link SuperSend campaign</Btn>
          </div>
        </div>
        {drawer}
      </Section>
    );
  }
  return (
    <Section title={`Buyer outreach · ${k.firms || 0} buyers`} collapsible id="deal.outreach"
      preview={`${k.emailed || 0} emailed · ${k.replied || 0} replied · ${k.nda_signed || 0} NDA signed · ${k.passed || 0} passed`}
      action={(
        <span style={{ display: 'inline-flex', gap: 6 }}>
          <Btn size="xs" variant="outline" icon={<Icon.Upload size={11} />} onClick={() => setImporting(true)}>Upload</Btn>
          <Btn size="xs" variant="accent" onClick={onOpen}>Open tracker</Btn>
        </span>
      )}>
      <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <OutreachKpis k={k} compact />
        <OutreachCampaignLine campaigns={campaigns} onLink={() => setLinking(true)} />
      </div>
      {drawer}
    </Section>
  );
};

// The process's deadlines: letter sent, IOIs due, LOIs due — editable, with
// a countdown on what's next.
const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return Math.round((d - t) / 86400000);
};
const OutreachProcessLine = ({ deal, process, onSaved, readOnly = false }) => {
  const p = process || {};
  const save = async (k, v) => {
    try { await window.AtlasApi.updateOutreachProcess(deal.id, { [k]: v || null }); onSaved && onSaved(); }
    catch (e) { pushToast({ type: 'error', title: 'Save failed', body: e.message }); throw e; }
  };
  const item = (k, label) => {
    const n = k === 'process_letter_date' ? null : daysUntil(p[k]);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <span style={{ color: 'var(--text-3)' }}>{label}</span>
        <EditableCell editable={!readOnly} type="date" value={p[k]} placeholder="set date"
          onSave={(v) => save(k, v)}
          displayAs={p[k] ? <span className="num" style={{ color: 'var(--text)' }}>{fmtOutreachDate(p[k])}</span> : undefined} />
        {n != null && (
          <span style={{ fontSize: 10.5, fontWeight: 600, color: n < 0 ? 'var(--text-4)' : n <= 3 ? 'var(--amber-fg, #b54708)' : 'var(--text-3)' }}>
            {n < 0 ? 'passed' : n === 0 ? 'today' : `in ${n} day${n === 1 ? '' : 's'}`}
          </span>
        )}
      </span>
    );
  };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', fontSize: 11.5 }}>
      <span style={{ color: 'var(--text-3)' }}>Process:</span>
      {item('process_letter_date', 'Letter sent')}
      {item('ioi_due_date', 'IOIs due')}
      {item('loi_due_date', 'LOIs due')}
    </div>
  );
};

// "SuperSend: <campaign>" chips, or the nudge to link one.
const OutreachCampaignLine = ({ campaigns, onLink, onUnlink }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 11.5, color: 'var(--text-3)' }}>
    {campaigns.length === 0 ? (
      <>
        <span>No SuperSend campaign linked</span>
        <Btn size="xs" variant="outline" icon={<Icon.Mail size={11} />} onClick={onLink}>Link campaign</Btn>
      </>
    ) : (
      <>
        <span>SuperSend:</span>
        {campaigns.map((c) => (
          <span key={c.id} title={`Linked${c.linked_by ? ' by ' + c.linked_by : ''}${c.linked_at ? ' · ' + fmtOutreachDate(c.linked_at) : ''}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999,
                     border: '1px solid var(--border)', background: 'var(--surface-2)', color: 'var(--text-2)' }}>
            <Icon.Mail size={10} /> {c.name || c.campaign_id}
            {onUnlink && (
              <button onClick={() => onUnlink(c)} title="Unlink this campaign"
                style={{ background: 'none', border: 'none', padding: 0, marginLeft: 2, cursor: 'pointer', color: 'var(--text-4)', lineHeight: 1 }}>×</button>
            )}
          </span>
        ))}
        {onLink && <Btn size="xs" variant="ghost" onClick={onLink}>+ Link another</Btn>}
      </>
    )}
  </div>
);

// ── Link a SuperSend campaign ──────────────────────────────────────────────
const OutreachCampaignDrawer = ({ deal, onClose, onLinked }) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [pick, setPick] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    window.AtlasApi.outreachCampaignCandidates(deal.id)
      .then((d) => { setData(d); if (d.suggested) setPick(d.suggested); })
      .catch((e) => setError(e.message || 'Could not list campaigns'));
  }, [deal.id]);
  const chosen = data && data.items.find((c) => c.campaign_id === pick);
  const link = async () => {
    if (!chosen) return;
    const ok = await window.showConfirm({
      title: `Link “${chosen.name}” to ${deal.name}?`,
      body: `Its ${chosen.recipients} recipients' emails, follow-ups and replies show on the tracker, and anyone emailed who isn't on it yet is added. `
        + 'From now on its sends and replies are treated as buyer outreach — never as seller marketing — and any seller-marketing '
        + 'follow-up tasks or statuses it already created are undone.',
      confirmLabel: 'Link campaign',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const res = await window.AtlasApi.linkOutreachCampaign(deal.id, { campaign_id: chosen.campaign_id, campaign_name: chosen.name });
      const bits = [
        res.recipients_added ? `${res.recipients_added} added to the tracker` : null,
        res.not_in_atlas ? `${res.not_in_atlas} recipient${res.not_in_atlas === 1 ? ' isn’t' : 's aren’t'} in Atlas yet — upload the list to add them` : null,
        res.tasks_cancelled ? `${res.tasks_cancelled} seller-marketing task${res.tasks_cancelled === 1 ? '' : 's'} cancelled` : null,
        res.statuses_restored ? `${res.statuses_restored} marketing status${res.statuses_restored === 1 ? '' : 'es'} restored` : null,
      ].filter(Boolean);
      pushToast({ type: 'success', title: 'Campaign linked', body: bits.join(' · ') || undefined });
      onLinked && onLinked();
      onClose();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not link', body: e.message });
    } finally { setBusy(false); }
  };
  return (
    <Drawer open onClose={onClose} width={560} title="Link SuperSend campaign" subtitle={`Buyer outreach for ${deal.name}`}
      footer={(
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
          <Btn size="sm" variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn size="sm" variant="accent" disabled={!chosen || !!(chosen && chosen.linked_to) || busy} onClick={link}>
            {busy ? 'Linking…' : 'Link campaign'}
          </Btn>
        </div>
      )}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {error && <div style={{ fontSize: 12, color: 'var(--rose-fg, #b42318)' }}>{error}</div>}
        {!data && !error && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
        {data && !data.items.length && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No SuperSend campaign has sent recently.</div>}
        {data && data.items.map((c) => (
          <label key={c.campaign_id} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 8,
            border: `1px solid ${pick === c.campaign_id ? 'var(--accent)' : 'var(--border)'}`,
            background: pick === c.campaign_id ? 'var(--accent-soft)' : 'var(--surface)',
            cursor: c.linked_to ? 'not-allowed' : 'pointer', opacity: c.linked_to ? 0.6 : 1,
          }}>
            <input type="radio" name="outreach-campaign" disabled={!!c.linked_to} checked={pick === c.campaign_id}
              onChange={() => setPick(c.campaign_id)} style={{ accentColor: 'var(--accent)' }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{c.name || c.campaign_id}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                {c.recipients} recipients{c.overlap ? ` · ${c.overlap} on this tracker` : ''} · {fmtOutreachDate(c.first_at)} – {fmtOutreachDate(c.last_at)}
                {c.linked_to ? ` · linked to ${c.linked_to.name}` : ''}
              </div>
            </div>
            {data.suggested === c.campaign_id && <Pill hue="green" sz="sm">likely</Pill>}
          </label>
        ))}
      </div>
    </Drawer>
  );
};

// ── Per-buyer timeline ─────────────────────────────────────────────────────
const TIMELINE_KIND = {
  email: { label: 'Email', hue: 'blue' }, campaign: { label: 'SuperSend', hue: 'violet' }, note: { label: 'Note', hue: 'slate' },
};
const OutreachTimelineDrawer = ({ deal, row, onClose, onChanged, readOnly = false }) => {
  const [data, setData] = React.useState(null);
  const [open, setOpen] = React.useState(() => new Set());
  const [showOther, setShowOther] = React.useState(false);
  const [busy, setBusy] = React.useState(null);
  const load = React.useCallback(() => {
    if (!row) return;
    window.AtlasApi.outreachThread(deal.id, row.firm.id)
      .then(setData)
      .catch(() => setData({ items: [] }));
  }, [deal.id, row && row.firm.id]);
  React.useEffect(() => { setData(null); setShowOther(false); load(); }, [load]);
  if (!row) return null;
  const all = (data && data.items) || [];
  // Email the tracker couldn't place: a new thread with a buyer that's also on
  // another live deal, not naming this one. One click files it here.
  const items = all.filter((i) => i.counted !== false);
  const other = all.filter((i) => i.counted === false);
  const file = async (i, belongs) => {
    setBusy(i.id);
    try {
      await window.AtlasApi.fileOutreachMail(deal.id, i.id, belongs);
      pushToast({ type: 'success', title: belongs ? 'Filed under this deal' : 'Taken off this deal' });
      load();
      onChanged && onChanged();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not file it', body: e.message });
    } finally { setBusy(null); }
  };
  const card = (i, n, { offDeal = false } = {}) => {
    const k = TIMELINE_KIND[i.kind] || TIMELINE_KIND.note;
    const key = i.id || `${i.kind}-${n}`;
    const long = (i.body || '').length > 360;
    const expanded = open.has(key);
    return (
      <div key={key} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px', background: 'var(--surface)', opacity: offDeal ? 0.85 : 1 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Pill hue={k.hue} sz="sm">{k.label}</Pill>
          {i.kind === 'email' && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>{i.direction === 'outbound' ? 'we wrote' : 'they wrote'}</span>}
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.title || 'Note'}</span>
          {i.interest && <Pill hue={i.interest === 'interested' ? 'green' : i.interest === 'not_interested' ? 'rose' : 'amber'} sz="sm">{i.interest.replace('_', ' ')}</Pill>}
          <span style={{ fontSize: 11, color: 'var(--text-4)', whiteSpace: 'nowrap' }}>{fmtOutreachDate(i.at)}</span>
        </div>
        {(i.who && i.kind !== 'note') && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>{i.who}</div>}
        {i.body && (
          <div style={{ fontSize: 12, color: 'var(--text-2)', whiteSpace: 'pre-wrap', lineHeight: 1.5, marginTop: 4 }}>
            {long && !expanded ? i.body.slice(0, 360).trimEnd() + '…' : i.body}
            {long && (
              <button onClick={() => setOpen((s) => { const x = new Set(s); x.has(key) ? x.delete(key) : x.add(key); return x; })}
                style={{ background: 'none', border: 'none', padding: 0, marginLeft: 6, cursor: 'pointer', font: 'inherit', fontSize: 11.5, color: 'var(--accent-text)' }}>
                {expanded ? 'less' : 'more'}
              </button>
            )}
          </div>
        )}
        {(i.url || (i.kind === 'email' && (offDeal || i.filed_here))) && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 4 }}>
            {i.url && <a href={i.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: 'var(--accent-text)' }}>Open in Outlook</a>}
            {offDeal && !readOnly && <Btn size="xs" variant="outline" disabled={busy === i.id} onClick={() => file(i, true)}>Belongs to this deal</Btn>}
            {!offDeal && i.filed_here && !readOnly && (
              <button disabled={busy === i.id} onClick={() => file(i, false)} title="You filed this email here — take it off"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 11, color: 'var(--text-4)' }}>
                Not this deal
              </button>
            )}
          </div>
        )}
      </div>
    );
  };
  return (
    <Drawer open onClose={onClose} width={640} title={row.firm.firm}
      subtitle={(data && data.people && data.people.length)
        ? data.people.map((p) => p.name || p.email).join(', ')
        : 'Outreach timeline'}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {!data && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Loading…</div>}
        {data && !items.length && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Nothing on this buyer for this deal yet.</div>}
        {items.map((i, n) => card(i, n))}
        {other.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <button onClick={() => setShowOther((v) => !v)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 12, color: 'var(--text-3)', textAlign: 'left' }}>
              {showOther ? '▾' : '▸'} {other.length} other email{other.length === 1 ? '' : 's'} with these people, not counted for this deal
              <span style={{ color: 'var(--text-4)' }}> — this buyer is also on another live deal and they don’t name this one</span>
            </button>
            {showOther && other.map((i, n) => card(i, n, { offDeal: true }))}
          </div>
        )}
      </div>
    </Drawer>
  );
};

// A suggested outcome under the row's status: accept (✓) sets it on the
// buyer, dismiss (✕) stops it being proposed again. The tooltip is the
// evidence — whose email, when, and the words that triggered it.
const OutreachSuggestion = ({ s, onAccept, onDismiss }) => {
  const [busy, setBusy] = React.useState(false);
  const tip = [`Suggested from ${s.source === 'supersend' ? 'SuperSend' : 'email'}${s.who ? ' · ' + s.who : ''}${s.at ? ' · ' + fmtOutreachDate(s.at) : ''}`,
               s.why ? `“${s.why}”` : null].filter(Boolean).join('\n');
  const act = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  return (
    <span title={tip} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, marginTop: 2, fontSize: 10.5,
                               color: 'var(--accent-text)', opacity: busy ? 0.5 : 1 }}>
      <Icon.Sparkle size={9} /> {s.label}?
      <button disabled={busy} onClick={() => act(onAccept)} title="Accept"
        style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: 'var(--green-fg, #067647)', fontWeight: 700 }}>✓</button>
      <button disabled={busy} onClick={() => act(onDismiss)} title="Not right — don’t suggest again"
        style={{ background: 'none', border: 'none', padding: '0 2px', cursor: 'pointer', color: 'var(--text-4)' }}>✕</button>
    </span>
  );
};

// ── Send tracker people into the deal's SuperSend campaign ─────────────────
// Only into a campaign linked to THIS deal. The list is everyone on the
// tracker the campaign hasn't emailed yet (no SuperSend send, not passed,
// not bounced); the server re-checks the do-not-contact list and who's
// already in the campaign before adding anyone.
const PUSH_CHUNK = 25;   // routes/outreach.py PUSH_CHUNK
const PUSH_STATUS = { added: ['added', 'green'], already: ['already in it', 'slate'], skipped: ['skipped', 'amber'], failed: ['failed', 'rose'] };
const OutreachPushDrawer = ({ deal, rows, campaigns, onClose, onDone }) => {
  const [linkId, setLinkId] = React.useState(campaigns[0] && campaigns[0].id);
  const eligible = rows.filter((r) => r.recipient_id && r.email && r.firm.interest_level !== 'passed'
    && !((r.activity || {}).sends > 0) && !(r.activity || {}).bounced_at);
  const [picked, setPicked] = React.useState(() => new Set(eligible.map((r) => r.recipient_id)));
  const [progress, setProgress] = React.useState(null);
  const [results, setResults] = React.useState(null);
  const link = campaigns.find((c) => c.id === linkId);
  const running = progress && !results;
  const ids = eligible.filter((r) => picked.has(r.recipient_id)).map((r) => r.recipient_id);
  const send = async () => {
    if (!ids.length || !link) return;
    const ok = await window.showConfirm({
      title: `Add ${ids.length} ${ids.length === 1 ? 'person' : 'people'} to “${link.name || link.campaign_id}”?`,
      body: 'They enter the campaign’s sequence in SuperSend — if the campaign is running, the first email goes out on its schedule. '
        + 'Anyone already in the campaign, on the do-not-contact list, or whose address bounced is skipped.',
      confirmLabel: 'Add to campaign',
    });
    if (!ok) return;
    const all = [];
    setProgress({ done: 0, total: ids.length });
    for (let i = 0; i < ids.length; i += PUSH_CHUNK) {
      const chunk = ids.slice(i, i + PUSH_CHUNK);
      try {
        const res = await window.AtlasApi.pushOutreachCampaign(deal.id, link.id, chunk);
        all.push(...res.results);
      } catch (e) {
        chunk.forEach((id) => all.push({ recipient_id: id, status: 'failed', detail: e.message }));
      }
      setProgress({ done: Math.min(i + PUSH_CHUNK, ids.length), total: ids.length });
    }
    setResults(all);
    const added = all.filter((r) => r.status === 'added').length;
    pushToast({ type: all.some((r) => r.status === 'failed') ? 'warning' : 'success',
                title: `${added} added to SuperSend`, body: `${all.length - added} not added — see the list for why.` });
    onDone && onDone();
  };
  const byId = Object.fromEntries((results || []).map((r) => [r.recipient_id, r]));
  return (
    <Drawer open onClose={() => { if (!running) onClose(); }} width={620} title="Send to SuperSend"
      subtitle={`Buyer outreach for ${deal.name}`}
      footer={(
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{results ? `${results.filter((r) => r.status === 'added').length} added` : `${ids.length} selected`}</span>
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" disabled={running} onClick={onClose}>{results ? 'Done' : 'Cancel'}</Btn>
          {!results && (
            <Btn size="sm" variant="accent" disabled={running || !ids.length || !link} onClick={send}>
              {running ? `Adding… ${progress.done} / ${progress.total}` : `Add ${ids.length} to the campaign`}
            </Btn>
          )}
        </div>
      )}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {campaigns.length > 1 && (
          <window.Picker value={linkId} onChange={setLinkId} disabled={running || !!results} width="100%" placeholder="Campaign"
            options={campaigns.map((c) => ({ value: c.id, label: c.name || c.campaign_id }))} />
        )}
        <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.55 }}>
          {campaigns.length === 1 && <>Into <b>{link && (link.name || link.campaign_id)}</b>. </>}
          Untick anyone who shouldn’t go.
        </div>
        {!eligible.length && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Everyone on the tracker has already been emailed by the campaign (or passed).</div>}
        {eligible.map((r) => {
          const res = byId[r.recipient_id];
          return (
            <label key={r.recipient_id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '4px 0', borderBottom: '1px solid var(--border-2, var(--border))' }}>
              <input type="checkbox" checked={picked.has(r.recipient_id)} disabled={running || !!results}
                onChange={() => setPicked((s) => { const n = new Set(s); n.has(r.recipient_id) ? n.delete(r.recipient_id) : n.add(r.recipient_id); return n; })}
                style={{ accentColor: 'var(--accent)' }} />
              <span style={{ fontWeight: 500, color: 'var(--text)' }}>{r.name || r.email}</span>
              <span style={{ color: 'var(--text-3)' }}>{r.firm.firm}</span>
              <span style={{ color: 'var(--text-4)', marginLeft: 'auto' }}>{r.email}</span>
              {res && <Pill hue={(PUSH_STATUS[res.status] || [])[1] || 'slate'} sz="sm" title={res.detail || undefined}>
                {(PUSH_STATUS[res.status] || [res.status])[0]}{res.status === 'skipped' && res.detail ? ` · ${res.detail}` : ''}
              </Pill>}
            </label>
          );
        })}
      </div>
    </Drawer>
  );
};

// ── Add one person ─────────────────────────────────────────────────────────
const OutreachAddDrawer = ({ deal, open, onClose, onAdded }) => {
  const [contactId, setContactId] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (open) { setContactId(''); setBusy(false); } }, [open]);
  const add = async () => {
    if (!contactId) return;
    setBusy(true);
    try {
      const res = await window.AtlasApi.addOutreachRecipient(deal.id, { contact_id: contactId });
      pushToast({ type: 'success', title: res.already ? 'Already on the tracker' : 'Added to the tracker' });
      onAdded && onAdded();
      onClose();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not add', body: e.message });
    } finally { setBusy(false); }
  };
  return (
    <Drawer open={open} onClose={onClose} width={440} widthKey="outreachAdd" title="Add a person"
      subtitle="Their firm joins the deal’s buyer list"
      footer={(
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
          <Btn size="sm" variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn size="sm" variant="accent" disabled={!contactId || busy} onClick={add}>{busy ? 'Adding…' : 'Add'}</Btn>
        </div>
      )}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Person (needs an email and a company)</div>
        <Combobox value={contactId} onChange={(e) => setContactId(e.target.value)} searchable
          loadOptions={window.searchContactOptions} placeholder="Search people…" />
      </div>
    </Drawer>
  );
};

// ── The tracker ────────────────────────────────────────────────────────────
// Column widths; the firm column is sticky on the left.
const OC = { firm: 220, bstatus: 110, category: 140, person: 200, email: 220, first: 88, fu: 100, reply: 158,
             call: 126, status: 176, nda: 120, cim: 100, ioi: 116, interest: 128, bs: 96, loi: 116, act: 36 };
const INTEREST_HUE = { interested: 'green', not_interested: 'rose', follow_up: 'amber' };
const todayIso = () => window.localISODate();

// CIM sent / LOI received: the tick stamps today, the date next to it is its
// own field so a send from last week can be back-dated.
const OutreachDoneDate = ({ done, date, doneLabel, tickTitle, untickTitle, onTick, onDate, editable = true }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: done ? 'var(--text)' : 'var(--text-4)' }}>
    <input type="checkbox" checked={!!done} title={done ? untickTitle : tickTitle} disabled={!editable}
      style={{ accentColor: 'var(--accent)', cursor: editable ? 'pointer' : 'default', margin: 0 }}
      onChange={(e) => onTick(e.target.checked).catch(() => {})} />
    {done
      ? <EditableCell editable={editable} type="date" value={date} onSave={onDate}
          displayAs={<span className="num" title="Change the date">{fmtOutreachDate(date) || doneLabel}</span>} />
      : 'no'}
  </span>
);

// The Outcome column is editable: picking one sets the buyer's underlying
// fields (NDA / CIM / LOI / pass), the same ones the NDA / CIM / Interest
// columns edit. Moving a passed buyer forward reopens it.
const OUTCOME_OPTS = [
  { value: 'nda_sent', label: 'NDA sent', hue: 'amber' },
  { value: 'nda_redlined', label: 'NDA redlined', hue: 'amber' },
  { value: 'nda_signed', label: 'NDA signed', hue: 'green' },
  { value: 'cim_sent', label: 'CIM sent', hue: 'green' },
  { value: 'ioi', label: 'IOI received', hue: 'teal' },
  { value: 'mgmt', label: 'Mgmt meeting', hue: 'teal' },
  { value: 'loi', label: 'LOI received', hue: 'green' },
  { value: 'passed:not_interested', label: 'Passed · not interested', hue: 'rose' },
  { value: 'passed:too_small', label: 'Passed · too small', hue: 'rose' },
  { value: 'passed:geography', label: 'Passed · geography', hue: 'rose' },
  { value: 'passed:valuation', label: 'Passed · valuation', hue: 'rose' },
  { value: 'passed:wrong_industry', label: 'Passed · wrong industry', hue: 'rose' },
  { value: 'passed:other', label: 'Passed · other', hue: 'rose' },
];
const outcomeValue = (f) => (f.interest_level === 'passed' ? `passed:${f.pass_reason || 'other'}`
  : f.loi_submitted ? 'loi' : f.bs_call_date ? 'mgmt' : f.ioi_date ? 'ioi' : f.cim_sent ? 'cim_sent'
  : ['sent', 'redlined', 'signed'].includes(f.nda_status) ? `nda_${f.nda_status}` : '');
const outcomePatch = (v, f) => {
  if (v === 'reopen') return { interest_level: null, pass_reason: null };
  if (v.startsWith('passed:')) return { interest_level: 'passed', pass_reason: v.split(':')[1] };
  const reopen = f.interest_level === 'passed' ? { interest_level: null, pass_reason: null } : {};
  if (v.startsWith('nda_')) {
    const nda = v.slice(4);
    return { ...reopen, nda_status: nda, ...(nda === 'signed' && !f.nda_signed_date ? { nda_signed_date: todayIso() } : {}) };
  }
  if (v === 'cim_sent') return { ...reopen, cim_sent: true, cim_sent_date: f.cim_sent_date || todayIso() };
  if (v === 'loi') return { ...reopen, loi_submitted: true, loi_date: f.loi_date || todayIso() };
  if (v === 'ioi') return { ...reopen, ioi_date: f.ioi_date || todayIso() };
  if (v === 'mgmt') return { ...reopen, bs_call_date: f.bs_call_date || todayIso() };
  return {};
};
const CALL_OUTCOME_LABEL = (o) => (o || '').replace(/_/g, ' ');
const OC_W = Object.values(OC).reduce((a, b) => a + b, 0);

// `embedded` (the Buyers workspace's Outreach tab, views/buyers_workspace.jsx):
// no page header (breadcrumb, title, KPIs, process line: the workspace shows
// those), a compact toolbar, and a dimmed '—' where the page says more.
// `readOnly`: no write affordance (viewers). `tracker`: the workspace's own
// { data, error, reload, retry } — one state for both tabs; the hook still
// runs, with no deal, so the hook set never changes. `active` false (the
// workspace closed): its drawers close. Defaults = the page.
const OutreachTracker = ({ deal, company, onBack, embedded = false, readOnly = false, tracker = null, active = true }) => {
  const device = window.useDevice();
  const own = useOutreach(tracker ? null : deal && deal.id);
  const { data, error, reload } = tracker || own;
  const retry = (tracker && tracker.retry) || reload;   // a load-error Retry is not a write
  const [q, setQ] = React.useState('');
  const [filter, setFilter] = React.useState('all');
  const [category, setCategory] = React.useState('');
  const [importing, setImporting] = React.useState(false);
  const [adding, setAdding] = React.useState(false);
  const [notesRow, setNotesRow] = React.useState(null);
  const [linking, setLinking] = React.useState(false);
  const [pushing, setPushing] = React.useState(false);
  React.useEffect(() => {
    if (!active) { setImporting(false); setAdding(false); setNotesRow(null); setLinking(false); setPushing(false); }
  }, [active]);

  const rows = (data && data.rows) || [];
  const campaigns = (data && data.campaigns) || [];
  const unlink = async (c) => {
    const ok = await window.showConfirm({
      title: `Unlink “${c.name || c.campaign_id}”?`,
      body: 'The people stay on the tracker, but its emails and replies stop showing here — and its future events run as ordinary marketing again.',
      confirmLabel: 'Unlink', danger: true,
    });
    if (!ok) return;
    try { await window.AtlasApi.unlinkOutreachCampaign(deal.id, c.id); reload(); }
    catch (e) { pushToast({ type: 'error', title: 'Could not unlink', body: e.message }); }
  };
  const categories = React.useMemo(
    () => [...new Set(rows.map((r) => r.firm.category).filter(Boolean))].sort(),
    [rows]
  );
  const pred = (OUTREACH_FILTERS.find(([k]) => k === filter) || [])[2];
  const needle = q.trim().toLowerCase();
  const shown = rows.filter((r) => (!pred || pred(r))
    && (!category || r.firm.category === category)
    && (!needle || [r.firm.firm, r.name, r.email, r.platform_name, r.title].some((v) => (v || '').toLowerCase().includes(needle))));
  // Firm-level chips count buyers, like the KPIs above; the rest count people.
  const firmChips = new Set(['nda', 'nda_signed', 'bids', 'cim', 'passed']);
  const counts = Object.fromEntries(OUTREACH_FILTERS.map(([k, , p]) => {
    const hit = p ? rows.filter(p) : rows;
    return [k, firmChips.has(k) ? new Set(hit.map((r) => r.firm.id)).size : hit.length];
  }));

  const patchFirm = async (firmId, patch) => {
    try {
      await window.AtlasApi.updateBuyerEngagement(firmId, patch);
      await reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
      throw e;
    }
  };
  const patchPerson = async (id, patch) => {
    try {
      await window.AtlasApi.updateOutreachRecipient(id, patch);
      await reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Save failed', body: e.message });
      throw e;
    }
  };
  const dismissSuggestion = async (firmId, key) => {
    try { await window.AtlasApi.dismissOutreachSuggestion(firmId, key); reload(); }
    catch (e) { pushToast({ type: 'error', title: 'Could not dismiss', body: e.message }); }
  };
  const removePerson = async (r) => {
    const ok = await window.showConfirm({
      title: `Take ${r.name || r.email} off the tracker?`,
      body: `${r.firm.firm} stays on the deal’s buyer list, and the person record is untouched.`,
      confirmLabel: 'Remove', danger: true,
    });
    if (!ok) return;
    try {
      await window.AtlasApi.removeOutreachRecipient(r.recipient_id);
      reload();
    } catch (e) { pushToast({ type: 'error', title: 'Could not remove', body: e.message }); }
  };

  const exportRows = () => {
    window.exportCsv(`${deal.name} — outreach tracker.csv`, [
      { key: 'buyer_status', label: 'Buyer Status' }, { key: 'category', label: 'Category' },
      { key: 'firm', label: 'Company Name' }, { key: 'platform', label: 'Platform' },
      { key: 'name', label: 'Full Name' }, { key: 'title', label: 'Position/Title' }, { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' }, { key: 'first_email', label: 'First Email' },
      { key: 'fu1', label: 'SS Follow-up Email' }, { key: 'fu2', label: 'SS Follow-up Email 2' },
      { key: 'bounced', label: 'Undeliverable/SPAM' }, { key: 'replied', label: 'Replied' }, { key: 'reply_read', label: 'Reply (SuperSend)' },
      { key: 'status', label: 'Outcome' }, { key: 'nda', label: 'NDA' }, { key: 'nda_signed', label: 'NDA Signed' },
      { key: 'cim', label: 'CIM Sent' }, { key: 'interest', label: 'Interest' }, { key: 'pass_reason', label: 'Pass reason' },
      { key: 'ioi', label: 'IOI' }, { key: 'ioi_range', label: 'IOI range' },
      { key: 'bs_call', label: 'Mgmt meeting' }, { key: 'loi', label: 'LOI' }, { key: 'loi_amount', label: 'LOI amount' },
    ], shown.map((r) => ({
      buyer_status: (BUYER_STATUS_LABEL[r.firm.buyer_status_effective] || {}).label || '',
      category: r.firm.category || '', firm: r.firm.firm, platform: r.platform_name || '',
      name: r.name || '', title: r.title || '', email: r.email || '', phone: r.phone || '',
      first_email: String((r.activity && r.activity.first_email) || r.first_email_date || '').slice(0, 10),
      fu1: String((r.activity && r.activity.followup_1) || '').slice(0, 10),
      fu2: String((r.activity && r.activity.followup_2) || '').slice(0, 10),
      bounced: String((r.activity && r.activity.bounced_at) || '').slice(0, 10),
      replied: String((r.activity && r.activity.replied_at) || '').slice(0, 10),
      reply_read: ((r.activity && r.activity.interest) || '').replace('_', ' '),
      status: r.status_label, nda: r.firm.nda_status || '', nda_signed: r.firm.nda_signed_date || '',
      cim: r.firm.cim_sent ? (r.firm.cim_sent_date || 'Yes') : '', interest: r.firm.interest_level || '',
      pass_reason: r.firm.pass_reason || '', bs_call: r.firm.bs_call_date || '',
      ioi: r.firm.ioi_date || '', ioi_range: fmtRange(r.firm.ioi_low, r.firm.ioi_high) || '',
      loi: r.firm.loi_submitted ? (r.firm.loi_date || 'Yes') : '', loi_amount: r.firm.loi_amount != null ? r.firm.loi_amount : '',
    })));
  };

  const head = (label, w, extra = {}) => (
    <div style={{ width: w, flexShrink: 0, padding: '0 10px', display: 'flex', alignItems: 'center', ...extra }}>{label}</div>
  );
  // Phone outreach: put people on the Calling page's queue (PhoneBurner).
  const queueCalls = async (list) => {
    const ids = [...new Set(list.map((r) => r.contact_id).filter(Boolean))];
    if (!ids.length) return;
    if (ids.length > 1) {
      const ok = await window.showConfirm({
        title: `Queue ${ids.length} people to call?`,
        body: 'They join the Calling page’s queue for PhoneBurner, noted as this deal’s buyer outreach.',
        confirmLabel: 'Queue calls',
      });
      if (!ok) return;
    }
    try {
      await window.AtlasApi.dialerQueue({ contact_ids: ids, note: `Buyer outreach · ${deal.name}`.slice(0, 500) });
      pushToast({ type: 'success', title: ids.length === 1 ? 'Queued to call' : `${ids.length} queued to call`,
                  body: 'Send them from the Calling page.' });
      reload();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not queue', body: e.message });
    }
  };
  const callable = shown.filter((r) => r.contact_id && r.phone && r.status !== 'passed'
    && !(r.activity && (r.activity.call_queued || r.activity.do_not_call)));
  let lastFirm = null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--surface-2)' }}>
      {embedded ? (
        <div data-outreach-toolbar="" style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '8px 16px',
          display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {data && campaigns.length > 0 && (
            <OutreachCampaignLine campaigns={campaigns} onLink={readOnly ? undefined : () => setLinking(true)} onUnlink={readOnly ? undefined : unlink} />
          )}
          {data && campaigns.length === 0 && !readOnly && (
            <Btn size="xs" variant="outline" icon={<Icon.Mail size={11} />} onClick={() => setLinking(true)}>Link campaign</Btn>
          )}
          <div style={{ flex: 1 }} />
          {!readOnly && campaigns.length > 0 && (
            <Btn size="sm" variant="outline" icon={<Icon.Mail size={12} />} onClick={() => setPushing(true)}
              title="Add tracker people the campaign hasn't emailed yet to the deal's SuperSend campaign">Send to SuperSend</Btn>
          )}
          {!readOnly && <Btn size="sm" variant="outline" icon={<Icon.Plus size={12} />} onClick={() => setAdding(true)}>Add person</Btn>}
          {!readOnly && <Btn size="sm" variant="outline" icon={<Icon.Upload size={12} />} onClick={() => setImporting(true)}>Upload list</Btn>}
          <Btn size="sm" variant="outline" icon={<Icon.Download size={12} />} disabled={!shown.length} onClick={exportRows}>Export</Btn>
        </div>
      ) : (
      <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '10px 16px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11.5, color: 'var(--text-3)' }}>
          <button onClick={onBack} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-3)', padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5 }}>
            <Icon.ChevronL size={11} /> {deal.name}
          </button>
          <Icon.ChevronR size={11} color="var(--text-4)" />
          <span>Buyer outreach</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <div style={{ fontSize: 18, fontWeight: 650, color: 'var(--text)', fontFamily: 'var(--font-display)' }}>
              Buyer outreach · {deal.name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>
              {(company || {}).name ? `${company.name} · ` : ''}{deal.owner && deal.owner !== '—' ? deal.owner : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {campaigns.length > 0 && (
              <Btn size="sm" variant="outline" icon={<Icon.Mail size={12} />} onClick={() => setPushing(true)}
                title="Add tracker people the campaign hasn't emailed yet to the deal's SuperSend campaign">Send to SuperSend</Btn>
            )}
            <Btn size="sm" variant="outline" icon={<Icon.Plus size={12} />} onClick={() => setAdding(true)}>Add person</Btn>
            <Btn size="sm" variant="outline" icon={<Icon.Upload size={12} />} onClick={() => setImporting(true)}>Upload list</Btn>
            <Btn size="sm" variant="outline" icon={<Icon.Download size={12} />} disabled={!shown.length} onClick={exportRows}>Export</Btn>
          </div>
        </div>
        {data && <div style={{ marginTop: 12 }}><OutreachKpis k={data.kpis} /></div>}
        {data && (
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <OutreachCampaignLine campaigns={campaigns} onLink={() => setLinking(true)} onUnlink={unlink} />
            <OutreachProcessLine deal={deal} process={data.process} onSaved={reload} />
          </div>
        )}
      </div>
      )}

      <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', borderBottom: '1px solid var(--border)', background: 'var(--surface)' }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 8, top: 7, color: 'var(--text-4)' }}><Icon.Search size={12} /></span>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search buyers, people, emails…"
            style={{ padding: '5px 10px 5px 26px', fontSize: 12, border: '1px solid var(--border)', borderRadius: 6, width: 240, background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit' }} />
        </div>
        {OUTREACH_FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} style={{
            padding: '4px 9px', fontSize: 11.5, borderRadius: 999, cursor: 'pointer', fontFamily: 'inherit',
            border: `1px solid ${filter === k ? 'var(--accent)' : 'var(--border)'}`,
            background: filter === k ? 'var(--accent-soft)' : 'var(--surface)',
            color: filter === k ? 'var(--accent-text)' : 'var(--text-2)', fontWeight: filter === k ? 600 : 500,
          }}>{label} <span className="num" style={{ color: 'var(--text-4)' }}>{counts[k] || 0}</span></button>
        ))}
        {!readOnly && callable.length > 0 && (
          <Btn size="xs" variant="outline" icon={<Icon.Phone size={11} />} onClick={() => queueCalls(callable)}
            title="Put everyone shown here who has a phone (and hasn't passed) on the Calling page's queue">
            Queue {callable.length} to call
          </Btn>
        )}
        {categories.length > 1 && (
          <window.Picker value={category} onChange={setCategory} size="sm" placeholder="Category" emptyLabel="All categories"
            options={categories} />
        )}
      </div>

      <div style={{ flex: 1, overflow: 'auto' }}>
        {error && (embedded ? (
          <div data-outreach-error="" title={error} style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-4)' }}>
            —<Btn size="xs" variant="ghost" title="Retry" icon={<Icon.Refresh size={11} />} onClick={() => retry()} />
          </div>
        ) : <div style={{ padding: 16, fontSize: 12, color: 'var(--rose-fg, #b42318)' }}>{error}</div>)}
        {!data && !error && (embedded
          ? <div style={{ padding: 16, fontSize: 12, color: 'var(--text-4)' }}>—</div>
          : <div style={{ padding: 16, fontSize: 12, color: 'var(--text-3)' }}>Loading the tracker…</div>)}
        {data && !rows.length && (embedded ? <div style={{ padding: 16, fontSize: 12, color: 'var(--text-4)' }}>—</div> : (
          <div style={{ padding: 24, fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6, maxWidth: 560 }}>
            Nobody on the tracker yet.
          </div>
        ))}
        {data && rows.length > 0 && (
          <div style={{ minWidth: OC_W, fontSize: 12 }}>
            <div style={{ display: 'flex', height: 30, position: 'sticky', top: 0, zIndex: 2, background: 'var(--surface-2)', borderBottom: '1px solid var(--border)',
                          fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {head('Buyer', OC.firm, { position: 'sticky', left: 0, background: 'var(--surface-2)', zIndex: 3, paddingLeft: 16 })}
              {head('Status', OC.bstatus)}{head('Category', OC.category)}{head('Person', OC.person)}{head('Email / phone', OC.email)}
              {head('First email', OC.first)}{head('Follow-ups', OC.fu)}{head('Reply', OC.reply)}{head('Call', OC.call)}
              {head('Outcome', OC.status)}{head('NDA', OC.nda)}{head('CIM', OC.cim)}
              {head('IOI', OC.ioi)}{head('Interest', OC.interest)}
              {head(<span title="Management meeting / buyer–seller call">Mgmt mtg</span>, OC.bs)}{head('LOI', OC.loi)}{head('', OC.act)}
            </div>
            {shown.map((r) => {
              const f = r.firm;
              const a = r.activity || {};
              const firstOfFirm = lastFirm !== f.id;
              lastFirm = f.id;
              const bs = BUYER_STATUS_LABEL[f.buyer_status_effective];
              const cell = (w, children, extra = {}) => (
                <div style={{ width: w, flexShrink: 0, padding: '7px 10px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'flex-start', gap: 3, minWidth: 0, ...extra }}>{children}</div>
              );
              return (
                <div key={r.key} style={{ display: 'flex', minHeight: 46, borderTop: firstOfFirm ? '1px solid var(--border)' : '1px dashed var(--border-2, var(--border))', background: 'var(--surface)' }}>
                  {cell(OC.firm, firstOfFirm ? (
                    <>
                      <span onClick={() => window.AtlasNav?.goToCompany?.(f.buyer_company_id)} title={`Open ${f.firm}`}
                        style={{ fontWeight: 600, color: 'var(--text)', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.firm}</span>
                      {r.platform_name && <span style={{ fontSize: 10.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title="Platform named on the tracker">via {r.platform_name}</span>}
                    </>
                  ) : (r.platform_name && <span style={{ fontSize: 10.5, color: 'var(--text-4)' }}>via {r.platform_name}</span>),
                  { position: 'sticky', left: 0, background: 'var(--surface)', zIndex: 1, paddingLeft: 16, borderRight: '1px solid var(--border)' })}
                  {cell(OC.bstatus, firstOfFirm && (
                    <EditableCell editable={!readOnly} type="select" value={f.buyer_status || ''} options={[...BUYER_STATUS_OPTS, { value: '', label: '— clear —' }]}
                      onSave={(v) => patchFirm(f.id, { buyer_status: v || null })}
                      displayAs={bs ? <Pill hue={bs.hue} sz="sm">{bs.label}</Pill> : undefined} />
                  ))}
                  {cell(OC.category, firstOfFirm && (
                    <EditableCell editable={!readOnly} type="text" value={f.category || ''} onSave={(v) => patchFirm(f.id, { category: v || null })} />
                  ))}
                  {cell(OC.person, r.recipient_id ? (
                    <>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, width: '100%', minWidth: 0 }}>
                        <span style={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
                          <EditableCell editable={!readOnly} type="text" value={r.name || ''} placeholder={r.email}
                            onSave={(v) => patchPerson(r.recipient_id, { full_name: v })} />
                        </span>
                        {r.contact_id && (
                          <button onClick={() => window.AtlasNav?.goToContact?.(r.contact_id)} title="Open the person"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-4)', flexShrink: 0 }}>↗</button>
                        )}
                      </span>
                      <span style={{ fontSize: 10.5, color: 'var(--text-3)', width: '100%' }}>
                        <EditableCell editable={!readOnly} type="text" value={r.title || ''} placeholder="—"
                          onSave={(v) => patchPerson(r.recipient_id, { title: v })} />
                      </span>
                    </>
                  ) : <span style={{ color: 'var(--text-4)', fontSize: 11 }}>no one yet</span>)}
                  {cell(OC.email, r.email && (
                    <>
                      <a href={`mailto:${r.email}`} style={{ color: 'var(--accent-text)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }} title={r.email}>{r.email}</a>
                      <span style={{ fontSize: 10.5, color: 'var(--text-3)', width: '100%' }}>
                        {/* Touch: type 'phone' — a tap dials (EditableCell's tel: read
                            view) with the editor behind its pencil. */}
                        <EditableCell editable={!readOnly} type={device.touch ? 'phone' : 'text'} value={r.phone || ''} placeholder="—"
                          onSave={(v) => patchPerson(r.recipient_id, { office_phone: v })} />
                      </span>
                    </>
                  ))}
                  {cell(OC.first, r.recipient_id && (a.sends > 0
                    // SuperSend saw the send — its date, not editable.
                    ? <span className="num" title={`First SuperSend email ${fmtOutreachDate(a.first_email)}`}>{fmtOutreachDate(a.first_email)}</span>
                    : <EditableCell editable={!readOnly} type="date" value={r.first_email_date}
                        onSave={(v) => patchPerson(r.recipient_id, { first_email_date: v })}
                        displayAs={r.first_email_date ? <span className="num" title="Entered by hand (SuperSend has no send)">{fmtOutreachDate(r.first_email_date)}</span> : undefined} />
                  ))}
                  {cell(OC.fu, r.recipient_id && (a.followup_1
                    ? <>
                        <span className="num" style={{ fontSize: 11.5 }}>1 · {fmtOutreachDate(a.followup_1)}</span>
                        {a.followup_2 && <span className="num" style={{ fontSize: 11.5 }}>2 · {fmtOutreachDate(a.followup_2)}</span>}
                      </>
                    : <span style={{ color: 'var(--text-4)', fontSize: 11 }}>{a.sends ? (a.replied_at || a.finished ? 'stopped' : 'none yet') : '—'}</span>
                  ))}
                  {cell(OC.reply, r.recipient_id && (() => {
                    // Reply status on top (SuperSend's read when it has one),
                    // the email history right under it — opens the timeline.
                    const mails = a.emails || 0;
                    const notes = firstOfFirm ? (f.note_count || 0) : 0;
                    const history = [mails ? `${mails} email${mails === 1 ? '' : 's'}` : null,
                                     notes ? `${notes} note${notes === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ');
                    return (
                      <>
                        {a.bounced_at
                          ? <Pill hue="amber" sz="sm">Bounced</Pill>
                          : a.interest
                            ? <Pill hue={INTEREST_HUE[a.interest] || 'slate'} sz="sm" maxW={OC.reply - 24}>{a.interest.replace('_', ' ')}</Pill>
                            : a.replied_at
                              ? <Pill hue="violet" sz="sm">Replied</Pill>
                              : <span style={{ color: 'var(--text-4)', fontSize: 11 }}>{a.sends ? 'No reply yet' : '—'}</span>}
                        {(history || a.sends || a.replied_at) && (
                          <button onClick={() => setNotesRow(r)} title="The email exchange, SuperSend sends and notes with this buyer"
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 11,
                                     color: 'var(--accent-text)', textAlign: 'left', whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {[a.replied_at ? fmtOutreachDate(a.last_inbound_at || a.replied_at) : null, history || 'timeline'].filter(Boolean).join(' · ')}
                          </button>
                        )}
                        {a.waiting_on_us && r.status !== 'passed' && (
                          <span style={{ fontSize: 10.5, color: 'var(--amber-fg, #b54708)', fontWeight: 600 }} title="Their latest message is newer than ours">waiting on us</span>
                        )}
                      </>
                    );
                  })())}
                  {cell(OC.call, r.recipient_id && (
                    <>
                      {a.last_call_at
                        ? <span className="num" style={{ fontSize: 11.5, color: 'var(--text)' }} title="Last call (PhoneBurner / call notes)">
                            {fmtOutreachDate(a.last_call_at)}{a.last_call_outcome ? ` · ${CALL_OUTCOME_LABEL(a.last_call_outcome)}` : ''}
                          </span>
                        : null}
                      {a.do_not_call
                        ? <Pill hue="rose" sz="sm">do not call</Pill>
                        : a.call_queued
                          ? <Pill hue="blue" sz="sm" title="On the Calling page's queue">in call queue</Pill>
                          : !readOnly && r.contact_id && r.phone
                            ? <Btn size="xs" variant="ghost" icon={<Icon.Phone size={10} />} onClick={() => queueCalls([r])}
                                title="Put on the Calling page's queue (PhoneBurner)">Queue call</Btn>
                            : !a.last_call_at && <span style={{ color: 'var(--text-4)', fontSize: 11 }}>{r.phone ? '—' : 'no phone'}</span>}
                    </>
                  ))}
                  {cell(OC.status, (
                    <>
                      {firstOfFirm
                        ? <EditableCell editable={!readOnly} type="select" value={outcomeValue(f)}
                            options={f.interest_level === 'passed'
                              ? [...OUTCOME_OPTS, { value: 'reopen', label: 'Back in play (clear the pass)' }]
                              : OUTCOME_OPTS}
                            onSave={(v) => (v ? patchFirm(f.id, outcomePatch(v, f)) : Promise.resolve())}
                            displayAs={<Pill hue={OUTREACH_STATUS_HUE[r.status] || 'slate'} sz="sm" maxW={OC.status - 34}>{r.status_label}</Pill>} />
                        : <Pill hue={OUTREACH_STATUS_HUE[r.status] || 'slate'} sz="sm" maxW={OC.status - 20}>{r.status_label}</Pill>}
                      {firstOfFirm && !readOnly && (f.suggestions || []).map((sg) => (
                        <OutreachSuggestion key={sg.key} s={sg} onAccept={() => patchFirm(f.id, sg.set).catch(() => {})}
                          onDismiss={() => dismissSuggestion(f.id, sg.key)} />
                      ))}
                    </>
                  ))}
                  {cell(OC.nda, firstOfFirm && (
                    <>
                      <EditableCell editable={!readOnly} type="select" value={f.nda_status || 'not_sent'} options={OUTREACH_NDA_OPTS}
                        onSave={(v) => patchFirm(f.id, { nda_status: v || 'not_sent' })}
                        displayAs={<Pill hue={(OUTREACH_NDA_OPTS.find((o) => o.value === (f.nda_status || 'not_sent')) || {}).hue || 'slate'} sz="sm">{(f.nda_status || 'not_sent').replace('_', ' ')}</Pill>} />
                      {f.nda_status === 'signed' && (
                        <span style={{ fontSize: 10 }}>
                          <EditableCell editable={!readOnly} type="date" value={f.nda_signed_date} placeholder="date?"
                            onSave={(v) => patchFirm(f.id, { nda_signed_date: v })}
                            displayAs={f.nda_signed_date ? <span style={{ fontSize: 10, color: 'var(--text-4)' }}>{fmtOutreachDate(f.nda_signed_date)}</span> : undefined} />
                        </span>
                      )}
                    </>
                  ))}
                  {cell(OC.cim, firstOfFirm && (
                    <OutreachDoneDate editable={!readOnly} done={f.cim_sent} date={f.cim_sent_date} doneLabel="sent"
                      tickTitle="Mark the CIM as sent today" untickTitle="CIM sent — untick to clear"
                      onTick={(on) => patchFirm(f.id, on
                        ? { cim_sent: true, cim_sent_date: f.cim_sent_date || todayIso() }
                        : { cim_sent: false, cim_sent_date: null })}
                      onDate={(v) => patchFirm(f.id, { cim_sent_date: v })} />
                  ))}
                  {cell(OC.ioi, firstOfFirm && (
                    <>
                      <EditableCell editable={!readOnly} type="date" value={f.ioi_date} placeholder="—"
                        onSave={(v) => patchFirm(f.id, { ioi_date: v })}
                        displayAs={f.ioi_date ? <span className="num">{fmtOutreachDate(f.ioi_date)}</span> : undefined} />
                      {f.ioi_date && (
                        <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
                          <EditableCell editable={!readOnly} type="text" value={fmtRange(f.ioi_low, f.ioi_high) || ''} placeholder="amount?"
                            onSave={(v) => { const r = parseRange(v); return patchFirm(f.id, { ioi_low: r.low, ioi_high: r.high }); }} />
                        </span>
                      )}
                    </>
                  ))}
                  {cell(OC.interest, firstOfFirm && (
                    <>
                      <EditableCell editable={!readOnly} type="select" value={f.interest_level}
                        options={[
                          { value: 'high', label: 'high', hue: 'green' }, { value: 'medium', label: 'medium', hue: 'amber' },
                          { value: 'low', label: 'low', hue: 'amber' }, { value: 'passed', label: 'passed', hue: 'rose' },
                          { value: '', label: '— clear —' },
                        ]}
                        onSave={(v) => patchFirm(f.id, { interest_level: v || null })}
                        displayAs={f.interest_level ? <Pill hue={f.interest_level === 'passed' ? 'rose' : f.interest_level === 'high' ? 'green' : 'amber'} sz="sm">{f.interest_level}</Pill> : undefined} />
                      {f.interest_level === 'passed' && (
                        <span style={{ fontSize: 10 }}>
                          <EditableCell editable={!readOnly} type="select" value={f.pass_reason} placeholder="reason?" options={OUTREACH_PASS_OPTS}
                            onSave={(v) => patchFirm(f.id, { pass_reason: v || null })}
                            displayAs={f.pass_reason ? <span style={{ fontSize: 10, color: 'var(--text-4)' }}>{f.pass_reason.replace('_', ' ')}</span> : undefined} />
                        </span>
                      )}
                    </>
                  ))}
                  {cell(OC.bs, firstOfFirm && (
                    <EditableCell editable={!readOnly} type="date" value={f.bs_call_date} onSave={(v) => patchFirm(f.id, { bs_call_date: v })}
                      displayAs={f.bs_call_date ? <span className="num">{fmtOutreachDate(f.bs_call_date)}</span> : undefined} />
                  ))}
                  {cell(OC.loi, firstOfFirm && (
                    <>
                      <OutreachDoneDate editable={!readOnly} done={f.loi_submitted} date={f.loi_date} doneLabel="yes"
                        tickTitle="Mark an LOI received today" untickTitle="LOI received — untick to clear"
                        onTick={(on) => patchFirm(f.id, on
                          ? { loi_submitted: true, loi_date: f.loi_date || todayIso() }
                          : { loi_submitted: false, loi_date: null })}
                        onDate={(v) => patchFirm(f.id, { loi_date: v })} />
                      {f.loi_submitted && (
                        <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
                          <EditableCell editable={!readOnly} type="text" value={f.loi_amount != null ? window.fmtMoney(f.loi_amount) : ''} placeholder="amount?"
                            onSave={(v) => { const r = parseRange(v); return patchFirm(f.id, { loi_amount: r.high }); }} />
                        </span>
                      )}
                    </>
                  ))}
                  {cell(OC.act, !readOnly && r.recipient_id && (
                    <button onClick={() => removePerson(r)} title="Take off the tracker"
                      style={{ background: 'none', border: 'none', padding: 2, cursor: 'pointer', color: 'var(--text-4)' }}>
                      <Icon.Trash size={12} />
                    </button>
                  ))}
                </div>
              );
            })}
            {!shown.length && (embedded ? <div style={{ padding: 16, fontSize: 12, color: 'var(--text-4)' }}>—</div> : <div style={{ padding: 16, fontSize: 12, color: 'var(--text-3)' }}>No rows match.</div>)}
          </div>
        )}
      </div>

      {importing && <window.OutreachImportDrawer deal={deal} open onClose={() => setImporting(false)} onDone={reload} />}
      <OutreachAddDrawer deal={deal} open={adding} onClose={() => setAdding(false)} onAdded={reload} />
      <OutreachTimelineDrawer deal={deal} row={notesRow} onClose={() => setNotesRow(null)} onChanged={reload} readOnly={readOnly} />
      {pushing && <OutreachPushDrawer deal={deal} rows={rows} campaigns={campaigns} onClose={() => setPushing(false)} onDone={reload} />}
      {linking && <OutreachCampaignDrawer deal={deal} onClose={() => setLinking(false)} onLinked={reload} />}
    </div>
  );
};

// ── Upload a buyer list / tracker ──────────────────────────────────────────
const OUTREACH_FIELD_LABEL = {
  buyer_status: 'Buyer status', category: 'Category', nda_status: 'NDA', nda_signed_date: 'NDA signed',
  cim_sent: 'CIM sent', cim_sent_date: 'CIM date', interest_level: 'Interest', pass_reason: 'Pass reason',
};
const OutreachImportDrawer = ({ deal, open, onClose, onDone }) => {
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [progress, setProgress] = React.useState(null);
  const [results, setResults] = React.useState(null);
  const [filter, setFilter] = React.useState('all');
  const [campaignHint, setCampaignHint] = React.useState(null);   // the SuperSend campaign these people were emailed by
  const [linkingHint, setLinkingHint] = React.useState(false);
  const fileRef = React.useRef(null);

  const reset = () => {
    setPreview(null); setError(null); setProgress(null); setResults(null); setFilter('all'); setCampaignHint(null);
    if (fileRef.current) fileRef.current.value = '';
  };
  const running = progress && !results;
  const close = () => { if (running) return; reset(); onClose(); };
  const pick = async (f) => {
    if (!f) return;
    reset();
    setBusy(true);
    try { setPreview(await window.AtlasApi.previewOutreachImport(deal.id, f)); }
    catch (e) { setError(e.message || 'Could not read the file'); }
    finally { setBusy(false); }
  };

  const rows = (preview && preview.rows) || [];
  const toApply = rows.filter((r) => r.status !== 'skip');
  const c = (preview && preview.counts) || {};
  const needsLook = rows.filter((r) => r.status === 'skip' || (r.warnings || []).length);
  const shown = filter === 'look' ? needsLook : filter === 'new' ? rows.filter((r) => (r.creates || []).length) : rows;

  const apply = async () => {
    const total = toApply.length;
    if (!total) return;
    const ok = await window.showConfirm({
      title: `Add ${total.toLocaleString()} ${total === 1 ? 'person' : 'people'} to the tracker?`,
      body: [
        c.new_companies ? `${c.new_companies} new buyer compan${c.new_companies === 1 ? 'y' : 'ies'} and ${c.new_people || 0} new people are created in Atlas.` : (c.new_people ? `${c.new_people} new people are created in Atlas.` : null),
        'Values already in Atlas are kept; blanks are filled and an NDA only moves forward.',
      ].filter(Boolean).join(' '),
      confirmLabel: 'Apply',
    });
    if (!ok) return;
    const all = [];
    setProgress({ done: 0, total });
    for (let i = 0; i < total; i += OUTREACH_CHUNK) {
      const chunk = toApply.slice(i, i + OUTREACH_CHUNK);
      try {
        const res = await window.AtlasApi.applyOutreachImport(deal.id, { filename: preview.filename, rows: chunk.map((r) => r.raw) });
        all.push(...res.results);
      } catch (e) {
        chunk.forEach((r) => all.push({ row: r.row, email: r.email, ok: false, error: e.message }));
      }
      setProgress({ done: Math.min(i + OUTREACH_CHUNK, total), total });
    }
    setResults(all);
    const failed = all.filter((r) => !r.ok).length;
    pushToast({ type: failed ? 'warning' : 'success', title: `${(all.length - failed).toLocaleString()} rows on the tracker`,
                body: failed ? `${failed} failed — download the results for why.` : undefined });
    if (onDone) onDone();
    // The uploaded people were probably emailed by a SuperSend campaign
    // already — offer to link it so follow-ups and replies fill in.
    try {
      const c = await window.AtlasApi.outreachCampaignCandidates(deal.id);
      const top = ((c && c.items) || [])[0];
      if (top && !top.linked_to && top.overlap >= 3) setCampaignHint(top);
    } catch (_) { /* no hint is fine */ }
  };
  const linkHint = async () => {
    const c = campaignHint;
    if (!c) return;
    setLinkingHint(true);
    try {
      const res = await window.AtlasApi.linkOutreachCampaign(deal.id, { campaign_id: c.campaign_id, campaign_name: c.name });
      const bits = [
        res.recipients_added ? `${res.recipients_added} more added to the tracker` : null,
        res.tasks_cancelled ? `${res.tasks_cancelled} seller-marketing task${res.tasks_cancelled === 1 ? '' : 's'} cancelled` : null,
        res.statuses_restored ? `${res.statuses_restored} marketing status${res.statuses_restored === 1 ? '' : 'es'} restored` : null,
      ].filter(Boolean);
      pushToast({ type: 'success', title: 'Campaign linked', body: bits.join(' · ') || undefined });
      setCampaignHint(null);
      if (onDone) onDone();
    } catch (e) {
      pushToast({ type: 'error', title: 'Could not link', body: e.message });
    } finally { setLinkingHint(false); }
  };

  const downloadResults = () => {
    const byRow = Object.fromEntries((results || []).map((r) => [r.row, r]));
    window.exportCsv(`tracker upload results — ${preview.filename}.csv`, [
      { key: 'row', label: 'Row' }, { key: 'email', label: 'Email' }, { key: 'firm', label: 'Filed under' },
      { key: 'result', label: 'Result' }, { key: 'detail', label: 'Detail' },
    ], rows.map((r) => {
      const res = byRow[r.row];
      return {
        row: r.row, email: r.email, firm: (r.firm || {}).name || '',
        result: res ? (res.ok ? 'applied' : 'failed') : r.status,
        detail: res && !res.ok ? res.error : [...(r.errors || []), ...(r.warnings || [])].join(' · '),
      };
    }));
  };

  const footer = preview && (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%' }}>
      {results ? (
        <>
          <span style={{ fontSize: 12, color: 'var(--text-2)' }}>
            {results.filter((r) => r.ok).length.toLocaleString()} applied{results.some((r) => !r.ok) ? ` · ${results.filter((r) => !r.ok).length} failed` : ''}
          </span>
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" icon={<Icon.Download size={12} />} onClick={downloadResults}>Download results</Btn>
          <Btn size="sm" variant="accent" onClick={close}>Done</Btn>
        </>
      ) : (
        <>
          <div style={{ flex: 1 }} />
          <Btn size="sm" variant="outline" disabled={running} onClick={close}>Cancel</Btn>
          <Btn size="sm" variant="accent" disabled={running || !toApply.length} onClick={apply}>
            {running ? `Applying… ${progress.done} / ${progress.total}` : `Add ${toApply.length.toLocaleString()} to the tracker`}
          </Btn>
        </>
      )}
    </div>
  );

  return (
    <Drawer open={open} onClose={close} width={780} widthKey="outreachImport" title="Upload buyer list"
      subtitle={`Buyer outreach for ${deal.name}`} footer={footer}>
      <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
        {!preview && (
          <>
            <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.6 }}>
              Columns it reads (by name, anywhere in the first rows): Company Name · Website · PE Owner · Buyer Status ·
              Category · Stage · Full Name (or First / Last) · Position/Title · Email · Mobile · Office Phone · First Email ·
              Outcome · Comment · NDA Signed · CIM Sent
            </div>
            <label style={{ border: '1px dashed var(--border)', borderRadius: 8, padding: '28px 16px', textAlign: 'center',
                            cursor: busy ? 'progress' : 'pointer', color: 'var(--text-2)', fontSize: 12.5 }}>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm,text/csv" style={{ display: 'none' }}
                disabled={busy} onChange={(e) => pick(e.target.files && e.target.files[0])} />
              {busy ? 'Reading the file…' : 'Choose a CSV or Excel file'}
            </label>
            {error && <div style={{ fontSize: 12, color: 'var(--rose-fg, #b42318)' }}>{error}</div>}
          </>
        )}
        {preview && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12 }}>
              <span style={{ fontWeight: 600, color: 'var(--text)' }}>{preview.filename}</span>
              <span style={{ color: 'var(--text-3)' }}>· header on row {preview.header_row} · {rows.length.toLocaleString()} rows</span>
              {!results && !running && (
                <Btn size="xs" variant="outline" onClick={() => { reset(); setTimeout(() => fileRef.current && fileRef.current.click(), 0); }}>Choose another file</Btn>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {c.add > 0 && <Pill hue="blue" sz="sm">{c.add} new on the tracker</Pill>}
              {c.update > 0 && <Pill hue="slate" sz="sm">{c.update} already on it</Pill>}
              <Pill hue="slate" sz="sm">{c.firms || 0} buyer firms</Pill>
              {c.new_companies > 0 && <Pill hue="amber" sz="sm">{c.new_companies} new companies</Pill>}
              {c.new_people > 0 && <Pill hue="amber" sz="sm">{c.new_people} new people</Pill>}
              {c.skip > 0 && <Pill hue="rose" sz="sm">{c.skip} skipped</Pill>}
            </div>
            {preview.ignored && preview.ignored.length > 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Not read: {preview.ignored.join(', ')}</div>
            )}
            {results && campaignHint && (
              <div style={{ border: '1px solid var(--accent)', background: 'var(--accent-soft)', borderRadius: 8, padding: '10px 12px',
                            display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
                <Icon.Mail size={14} color="var(--accent)" />
                <div style={{ flex: 1 }}>
                  <b style={{ color: 'var(--text)' }}>{campaignHint.overlap}</b> of these people were emailed by the SuperSend
                  campaign <b style={{ color: 'var(--text)' }}>{campaignHint.name}</b>. Link it and
                  it stops counting as seller marketing.
                </div>
                <Btn size="sm" variant="accent" disabled={linkingHint} onClick={linkHint}>{linkingHint ? 'Linking…' : 'Link it'}</Btn>
              </div>
            )}
            {preview.duplicates && preview.duplicates.length > 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                Second {preview.duplicates.join(' / ')} column{preview.duplicates.length === 1 ? '' : 's'} skipped — the first one is read.
              </div>
            )}
            <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)' }}>
              {[['all', `All (${rows.length})`], ['new', `Creates records (${rows.filter((r) => (r.creates || []).length).length})`],
                ['look', `Needs a look (${needsLook.length})`]].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} style={{
                  padding: '6px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12,
                  fontWeight: filter === k ? 600 : 500, color: filter === k ? 'var(--text)' : 'var(--text-3)',
                  borderBottom: filter === k ? '2px solid var(--text)' : '2px solid transparent', marginBottom: -1,
                }}>{l}</button>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {shown.slice(0, 400).map((r) => {
                const res = results && results.find((x) => x.row === r.row);
                const firm = r.firm || {};
                return (
                  <div key={r.row} style={{ padding: '7px 0', borderBottom: '1px solid var(--border-2, var(--border))', fontSize: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ color: 'var(--text-4)', minWidth: 42 }} className="num">row {r.row}</span>
                      <span style={{ fontWeight: 600, color: 'var(--text)' }}>{r.name || r.email || '(blank)'}</span>
                      {r.email && r.name && <span style={{ color: 'var(--text-3)' }}>{r.email}</span>}
                      {r.status === 'skip' && <Pill hue="rose" sz="sm">skipped</Pill>}
                      {res && <Pill hue={res.ok ? 'green' : 'rose'} sz="sm">{res.ok ? 'applied' : 'failed'}</Pill>}
                    </div>
                    {firm.name && (
                      <div style={{ marginLeft: 48, marginTop: 2, color: 'var(--text-2)' }}>
                        → {firm.id ? <a href={`#/companies/${firm.id}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text)', fontWeight: 500, textDecoration: 'none' }}>{firm.name}</a> : <b>{firm.name}</b>}
                        <span style={{ color: 'var(--text-4)' }}>
                          {firm.via === 'new' ? ' · new company' : ` · matched by ${firm.via === 'person' ? 'the person' : firm.via}`}
                          {firm.kind === 'sponsor' ? ' · sponsor' : ''}
                          {r.platform && r.platform.name ? ` · platform ${r.platform.name}` : ''}
                          {r.contact ? '' : ' · new person'}
                        </span>
                      </div>
                    )}
                    {r.changes && Object.keys(r.changes).length > 0 && (
                      <div style={{ marginLeft: 48, marginTop: 2, color: 'var(--text-3)' }}>
                        {Object.entries(r.changes).map(([k, v]) => `${OUTREACH_FIELD_LABEL[k] || k}: ${v === true ? 'yes' : String(v).replace('_', ' ')}`).join(' · ')}
                      </div>
                    )}
                    {[...(r.errors || []), ...(r.warnings || []), res && !res.ok ? res.error : null].filter(Boolean).map((w, i) => (
                      <div key={i} style={{ marginLeft: 48, marginTop: 2, fontSize: 11.5, color: 'var(--amber-fg, #b54708)' }}>{w}</div>
                    ))}
                  </div>
                );
              })}
              {shown.length > 400 && <div style={{ padding: '10px 0', fontSize: 11.5, color: 'var(--text-3)' }}>Showing the first 400 of {shown.length.toLocaleString()}.</div>}
              {!shown.length && <div style={{ padding: '14px 0', fontSize: 12, color: 'var(--text-3)' }}>Nothing here.</div>}
            </div>
          </>
        )}
      </div>
    </Drawer>
  );
};

window.OutreachSection = OutreachSection;
window.OutreachTracker = OutreachTracker;
window.OutreachImportDrawer = OutreachImportDrawer;
// The Buyers workspace (views/buyers_workspace.jsx) and the record cards
// (buyer_board.jsx) reuse these, read at render time.
Object.assign(window, { useOutreach, OutreachKpis, OutreachProcessLine, OutreachCampaignDrawer, OutreachDoneDate });
